import { Grid, IconButton, Link, Theme, Box, Stack, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { RiExternalLinkLine, RiBillFill } from 'react-icons/ri';
import { ReceiptAccountingRow } from 'common/types';
import { getPricingString } from 'common/utils/common';
import React from 'react';
import { Routes } from 'routing';
import { useTranslation } from 'services/localization';
import RentleDataGrid from 'components/datagrid/RentleDataGrid';
import { GridColDef, useGridApiRef } from '@mui/x-data-grid-pro';

interface ReceiptTableRow {
	id: string;
	name: string;
	excl_tax: string | '';
	tax: string | '';
	tax_percent: string | '';
	total: string;
	orderId: string;
	receiptId: string | null;
	receiptNumber: number | null;
	paymentId: string | null;
}
interface ReceiptTableColDef extends GridColDef {
	field: keyof ReceiptTableRow;
}
interface Props {
	rows: ReceiptAccountingRow[];
	startReceiptPrint: (receiptId: string) => void;
}

const ReceiptTable = ({ rows, startReceiptPrint }: Props) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const apiRef = useGridApiRef();

	const columns: ReceiptTableColDef[] = [
		{
			field: '__empty__' as any,
			headerName: '',
			minWidth: 40,
			width: 40,
		},
		{
			field: 'name',
			headerName: t('reports.accounting.receiptNr', 'Receipt nr.'),
			flex: 2,
			minWidth: 150,
			renderCell: ({ row }: { row: ReceiptTableRow }) => {
				return row.receiptId ? (
					<Stack direction="row" alignItems="center" justifyContent="center">
						<IconButton
							className={classes.hideOnPrint}
							aria-label="print"
							onClick={() => startReceiptPrint(row.receiptId!)}
							edge="start"
							size="small"
						>
							<RiBillFill size={20} />
						</IconButton>
						<Typography variant="body2">{row.receiptNumber || row.paymentId}</Typography>
					</Stack>
				) : null;
			},
		},
		{
			field: 'orderId',
			headerName: t('common:order.order', 'Order'),
			flex: 1,
			minWidth: 100,
			renderCell: ({ row }: { row: ReceiptTableRow }) => {
				return row.orderId ? (
					<Link href={Routes.BookingPage(row.orderId)} target="_blank" color="inherit">
						<Grid container justifyContent="flex-end" alignItems="center" spacing={0}>
							{t('common:order.order', 'Order')}
							<RiExternalLinkLine size={14} className={classes.openInNewIcon} />
						</Grid>
					</Link>
				) : null;
			},
		},
		{
			field: 'excl_tax',
			headerName: t('common:taxation.exclTax'),
			flex: 1,
			minWidth: 100,
		},
		{
			field: 'tax',
			headerName: t('common:taxation.tax'),
			flex: 1,
			minWidth: 100,
		},
		{
			field: 'tax_percent',
			valueGetter: () => '',
			headerName: `${t('common:taxation.tax')} %`,
			flex: 1,
			minWidth: 100,
		},
		{
			field: 'total',
			headerName: t('common:count.total', 'Total'),
			flex: 1,
			minWidth: 100,
		},
	];

	const receiptRowsToTableRow = (data: ReceiptAccountingRow[]): ReceiptTableRow[] => {
		return data.map((row, i) => ({
			id: row.receiptId ?? `${row.orderId}_${i}`,
			name: row.receiptNumber?.toString() ?? '',
			excl_tax: row.netAmount ? getPricingString(row.netAmount) : '',
			tax: row.taxAmount ? getPricingString(row.taxAmount) : '',
			tax_percent: '',
			total: row.totalAmount ? getPricingString(row.totalAmount) : '',
			orderId: row.orderId,
			receiptId: row.receiptId,
			receiptNumber: row.receiptNumber,
			paymentId: row.paymentId,
		}));
	};

	return (
		<Box mx={1} my={1}>
			<RentleDataGrid
				checkboxSelection={false}
				apiRef={apiRef}
				columns={columns}
				rows={receiptRowsToTableRow(rows)}
				autoHeight
				density="compact"
				pagination={false}
				disableColumnMenu
				disableColumnSelector
				hideFooter
				defaultSortModel={[
					{
						field: 'name',
						sort: 'asc',
					},
				]}
			/>
		</Box>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		openInNewIcon: {
			marginLeft: 2,
		},
		hideOnPrint: {
			'@media print': {
				display: 'none',
			},
		},
	}),
);

export default ReceiptTable;
